/**
 * Sidebar Bridge Service
 * Handles communication between React sidebar and Angular Router
 */

export interface MenuItemMapping {
  itemId: string;
  angularRoute: string;
  label: string;
}

export interface SidebarBridgeOptions {
  onNavigate?: (route: string) => void;
  getActiveRoute?: () => string;
}

export class SidebarBridge {
  private static instance: SidebarBridge;
  private navigationCallback?: (route: string) => void;
  private currentRoute: string = '';

  // Menu item to route mapping - MUST match actual Angular routes in dashboard-routing.module.ts
  private readonly routeMap: Record<string, string> = {
    // AA Ecosystem
    'account-aggregator': '/dashboard/aa-list/all',
    'fip': '/dashboard/fip-list',
    'purpose': '/dashboard/purpose',
    'consent-templates': '/dashboard/products',
    'consent-requests': '/dashboard/consentRequest',
    'fi-notifications': '/dashboard/finotifications',
    'data-fetch-request': '/dashboard/dataFetchRequest',
    'fi-data-purge': '/dashboard/fi-data-purge',
    'download-centre': '/dashboard/download-centre',

    // Bulk Operations
    'bulk-consent-request': '/dashboard/bulkConsentRequest',
    'bulk-data-fetch': '/dashboard/bulkDataFetch',
    'bulk-consent-revoke': '/dashboard/bulkConsentRevoke',
    'bulk-csv-upload': '/dashboard/bulkCsvUpload',

    // Analytics
    'insights': '/dashboard/insights',
    'analytics': '/dashboard/analytics',
    'pdf-analytics': '/dashboard/pdf-analytics',
    'user-analytics': '/dashboard/pfm/UserAnalytics',
    'early-warning-signals': '/dashboard/insights/ews',

    // Admin & Setup
    'settings': '/dashboard/settings',
    'manage-apps': '/dashboard/manage',
    'admin': '/dashboard/admin',
    'users-list': '/dashboard/admin/users-list',
    'webhooks': '/dashboard/webhook',

    // Reference & Docs
    'central-registry': '/dashboard/sync-entity',
    'integration-docs': '/dashboard/integration-docs',
    'mis': '/dashboard/mis',

    // MIS submenu items
    'mis-dashboard': '/dashboard/mis/dashboard',
    'mis-metrics': '/dashboard/mis/metrics',
    'mis-fip-funnel': '/dashboard/mis/fip-board',
    'sbi-metrics': '/dashboard/mis/sbiMetric',
    'mis-insights': '/dashboard/funnel',

    // Central Registry submenu items
    'fetch-record': '/dashboard/sync-entity',
    'cr-dashboard': '/dashboard/cr-activity-dashboard',
    'aa-fip-mapping': '/dashboard/aa-fip-mapping',
    'fip-saans': '/dashboard/fip-saans',
  };

  // Reverse map: route to itemId
  private readonly itemIdMap: Record<string, string>;

  private constructor() {
    // Create reverse mapping
    this.itemIdMap = Object.entries(this.routeMap).reduce((acc, [itemId, route]) => {
      acc[route] = itemId;
      return acc;
    }, {} as Record<string, string>);
  }

  static getInstance(): SidebarBridge {
    if (!SidebarBridge.instance) {
      SidebarBridge.instance = new SidebarBridge();
    }
    return SidebarBridge.instance;
  }

  /**
   * Initialize bridge with Angular callbacks
   */
  initialize(options: SidebarBridgeOptions) {
    this.navigationCallback = options.onNavigate;
    if (options.getActiveRoute) {
      this.currentRoute = options.getActiveRoute();
    }

    console.log('[SidebarBridge] Initialized with route:', this.currentRoute);
  }

  /**
   * Handle menu item selection from React sidebar
   * Maps itemId to Angular route and triggers navigation
   */
  handleMenuItemClick(itemId: string) {
    const route = this.routeMap[itemId];

    if (!route) {
      console.warn(`[SidebarBridge] No route mapping found for itemId: ${itemId}`);
      return;
    }

    console.log(`[SidebarBridge] Navigating from React: ${itemId} → ${route}`);

    // Trigger Angular navigation via callback
    if (this.navigationCallback) {
      this.navigationCallback(route);
    }

    // Also dispatch custom event as fallback
    window.dispatchEvent(new CustomEvent('react-sidebar-navigate', {
      detail: { itemId, route }
    }));
  }

  /**
   * Update active route from Angular Router
   * Returns the corresponding itemId to highlight in sidebar
   */
  updateActiveRoute(angularRoute: string): string | null {
    this.currentRoute = angularRoute;
    const itemId = this.itemIdMap[angularRoute];

    console.log(`[SidebarBridge] Route updated from Angular: ${angularRoute} → ${itemId || 'not found'}`);

    return itemId || null;
  }

  /**
   * Get current active item ID based on Angular route
   */
  getActiveItemId(): string | null {
    return this.itemIdMap[this.currentRoute] || null;
  }
}

// Export singleton instance
export const sidebarBridge = SidebarBridge.getInstance();
